import styled from '@emotion/native';
import { Col, Row } from 'components/base';
import { Button, useSkeletonContext, Visible } from 'design-system/base';
import { MARGIN } from 'design-system/theme';
import { useIsSmallerThan } from 'hooks/useResponsive';
import type React from 'react';
import type { PropsWithChildren } from 'react';

import { Header, type HeaderProps } from '../header';
import { ItemPlaceholder } from './Item';
import { SubHeadingPlaceholder } from './SubHeading';
import { Summary, type SummaryProps } from './Summary';

interface ListProps extends HeaderProps, SummaryProps, PropsWithChildren {
  header?: boolean;
}

interface PlaceholderProps {
  header?: boolean;
}

const Placeholder: React.FC<PlaceholderProps> = ({ header }) => (
  <>
    <SubHeadingPlaceholder />
    <ItemPlaceholder small={!header} />
    <ItemPlaceholder small={!header} />
    <ItemPlaceholder small={!header} />
  </>
);

const SummaryWrapper = styled(Row)({
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: MARGIN.s,
});

export const List: React.FC<ListProps> = ({
  primaryButtonText: buttonText,
  onPrimaryButtonPress: onPress,
  total,
  count,
  children,
  showPrimaryButton = true,
  header = true,
  ...headerProps
}) => {
  const { loading } = useSkeletonContext();
  const isSmall = useIsSmallerThan('sm');

  const showSecondaryButton = showPrimaryButton && isSmall;
  const showHeaderButton = showPrimaryButton && !isSmall;

  return (
    <Col flex={1}>
      <Visible if={header}>
        <Header
          {...headerProps}
          primaryButtonText={buttonText}
          onPrimaryButtonPress={onPress}
          showPrimaryButton={showHeaderButton}
        />
      </Visible>
      <Col>{loading ? <Placeholder header={header} /> : children}</Col>
      <SummaryWrapper>
        <Visible if={total}>
          <Summary total={total} count={count} />
        </Visible>
        <Visible if={showSecondaryButton}>
          {onPress ? (
            <Button
              variant="flat"
              onPress={onPress}
              iconPosition="right"
              icon="arrow-forward"
              text={buttonText}
            />
          ) : null}
        </Visible>
      </SummaryWrapper>
    </Col>
  );
};
